/**
 * Reduces a CPT table row to the values that count as user edits.
 * @module getComparableRowValues
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";

/**
 * Saved-value projection of a CPT row, mirroring the fields that
 * {@link transformTableDataToApiPayload} sends to the backend.
 */
export interface ComparableRowValues {
  jCode: string;
  startFrom: string;
  endAt: string;
  visits: string;
  remainingVisits: string;
  visitsAllowed: string;
  authStatusId: string;
  description: string;
  authNumber: string;
  pendingNumber: string;
  filingMode: string;
  filingDetail: string;
}

/**
 * Extracts the user-editable values from a CPT row for dirty tracking.
 *
 * Deliberately ignores derived and presentational state so re-selecting an
 * unchanged value does not mark the table dirty. Excluded: badge chrome
 * (`paRequired.text` / colours / `drug_name`), filing-info transition state
 * (`priorAuthStatusId`, `isRequired`, `isStatic`), validation flags
 * (`hasError`), editability flags (`allowInputModification`,
 * `allowEmptyValue`), identifiers, and the document / delete action cells.
 *
 * `description.text` is the single source for the description; the
 * searchable-card cell mirrors it and is not compared separately.
 *
 * @param row - CPT table row to project
 * @returns The comparable saved values for that row
 */
export const getComparableRowValues = (
  row: CptCodesTableRowModel,
): ComparableRowValues => ({
  jCode: row?.jCodes?.data ?? "",
  startFrom: row?.startFrom?.data ?? "",
  endAt: row?.endAt?.data ?? "",
  visits: row?.visits?.data ?? "",
  remainingVisits: row?.remainingVisits?.data ?? "",
  visitsAllowed: row?.visitsAllowed?.data ?? "",
  authStatusId: row?.paRequired?.currentStatusId ?? "",
  description: row?.description?.text ?? "",
  authNumber: row?.authorizationNumber?.data ?? "",
  pendingNumber: row?.pendingNumber?.data ?? "",
  filingMode: row?.filingInfo?.value ?? "",
  filingDetail: row?.filingInfo?.detail ?? "",
});

/**
 * Compares two CPT rows on their user-editable values only.
 *
 * @param row - Current row state
 * @param originalRow - Baseline row from the last load or save
 * @returns True when at least one saved value differs
 */
export const hasRowValueChanged = (
  row: CptCodesTableRowModel,
  originalRow: CptCodesTableRowModel,
): boolean => {
  const current = getComparableRowValues(row);
  const original = getComparableRowValues(originalRow);
  return (Object.keys(current) as (keyof ComparableRowValues)[]).some(
    (key) => current[key] !== original[key],
  );
};
